import React from 'react';
import { observer } from 'mobx-react';
import { Popover, Typography } from 'antd';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';

import { useMeasurement } from '@/hooks/useMeasurement';
import { useStores } from '@/hooks/use-stores';
import { metricsDetails } from '@/constants';
import { toTranslationFormat } from '@/utils/util-functions';

const TitleColumn = observer(({ title, alertRule }) => {
  const { devicesStore } = useStores();
  const { t } = useTranslation();
  const { tunit, tvalue } = useMeasurement();

  const timeUnitText = (unit, value) => {
    let text = '';
    switch (unit) {
      case 'hour':
        text = value === 1 ? t('hour') : t('hours');
        break;
      case 'minute':
        text = value === 1 ? t('minute') : t('minutes');
        break;
      case 'second':
        text = value === 1 ? t('second') : t('seconds');
        break;
      default:
    }

    return text;
  };

  const getRuleSentence = ({ id, conditions, deviceName, historyAttributes }) => {
    if (!conditions) return deviceName;

    const conditionsArr = [];
    const orOrAnd = conditions.match === 'match_any' ? t('or') : t('and');

    conditions.conditions.forEach(({ devicesIds, formula, metric, operator, threshold }, index, conditions) => {
      let i = 0;
      const nextCondition = conditions[index + 1];
      const metricText = t(toTranslationFormat(metricsDetails[metric]?.text));
      const metricUnit = metricsDetails[metric]?.unit;
      const devices = devicesIds
        ? formula.replace(/\{(.+?)\}/g, () => devicesStore.getChildOrParentDeviceName(devicesIds[i++]))
        : ' ';

      const conditionEl = (
        <span key={index}>
          <span className="bright-sky-blue bold">{metricText} </span>
          <span>({devices}) </span>
          <span>{t('is')} </span>
          <span className="mustard bold">{t(operator)} </span>
          <span className="bright-sky-blue bold">{tvalue(threshold, metricUnit)} </span>
          <span>{t(tunit(metricUnit))} </span>
          {nextCondition && <span className="purply">{orOrAnd} </span>}
        </span>
      );
      conditionsArr.push(conditionEl);
    });

    if (!isNil(historyAttributes)) {
      const durationEl = (
        <span key={id}>
          <span>{t('forMoreThan')} </span>
          <span className="bright-sky-blue bold">{historyAttributes.duration} </span>
          <span>{timeUnitText(historyAttributes.units, historyAttributes.duration)}</span>
        </span>
      );
      conditionsArr.push(durationEl);
    }

    return <span>{conditionsArr}</span>;
  };

  return (
    <div className="rule-description">
      {alertRule.complexFormula ? (
        <Popover
          placement={'topLeft'}
          content={
            <>
              <div>{t('You can only turn this alert rule off.')}</div>
              <div>
                {t('To modify please contact your system administrator or the Ecoplant support')}{' '}
                <Typography.Link rel="stylesheet" href="mailto:support@ecoplant.co">
                  support@ecoplant.co
                </Typography.Link>
              </div>
            </>
          }
        >
          <div className="rule-item-title">{title}</div>
        </Popover>
      ) : (
        <div className="rule-item-title">{title}</div>
      )}
      <div className="rule-item-detail">{getRuleSentence(alertRule)}</div>
    </div>
  );
});

export default TitleColumn;
